import { omit } from 'lodash';
import querystring from 'querystring';
import { createContext, ReactNode, useState } from 'react';
import { useAppSelector } from 'store';
import { composableFiltersState } from 'store/composableFiltersSlice';
import {
  useGetProductCrossSellQuery,
  useGetProductReplenishmentQuery,
  useGetRelationsProductListQuery,
  useGetRelationsSummaryQuery,
} from 'store/services/segmentsApi';
import type {
  JourneyProductList,
  ProductCrossSell,
  ProductReplenishment,
  RelationsSummary,
} from 'store/services/types';
import { useParseURLQueryParams } from 'utils';

interface RelationsInterface {
  selectedProduct: JourneyProductList | undefined;
  setSelectedProduct: React.Dispatch<
    React.SetStateAction<JourneyProductList | undefined>
  >;
  productList: JourneyProductList[] | undefined;
  productListIsLoading: boolean;
  crossSell: ProductCrossSell[] | undefined;
  crossSellIsLoading: boolean;
  replenishment: ProductReplenishment | undefined;
  replenishmentIsLoading: boolean;
  relationsSummary: RelationsSummary | undefined;
  relationsSummaryIsLoading: boolean;
}

export const RelationsContext = createContext({} as RelationsInterface);

export const RelationsContextProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [selectedProduct, setSelectedProduct] = useState<
    JourneyProductList | undefined
  >();

  const { rawQueryParams, serializedAllQueryParams } = useParseURLQueryParams();
  const { product_id, start_date, end_date, store_name } = rawQueryParams;

  const productListParams = querystring.stringify(
    omit(
      {
        ...rawQueryParams,
      },
      ['product_id']
    )
  );

  const {
    output: { filtersApiPayload },
  } = useAppSelector(composableFiltersState);

  const { data: productList, isFetching: productListLoading } =
    useGetRelationsProductListQuery(
      { serializedParams: productListParams, body: filtersApiPayload },
      {
        skip: !!!store_name || !!!start_date || !!!end_date,
      }
    );

  const { data: crossSell, isFetching: crossSellLoading } =
    useGetProductCrossSellQuery(
      {
        serializedParams: serializedAllQueryParams,
        productId: Number(product_id),
        body: filtersApiPayload,
      },
      {
        skip: !!!store_name || !!!start_date || !!!end_date || !!!product_id,
      }
    );

  const { data: replenishment, isFetching: replenishmentLoading } =
    useGetProductReplenishmentQuery(
      {
        serializedParams: serializedAllQueryParams,
        productId: Number(rawQueryParams?.product_id),
        body: filtersApiPayload,
      },
      {
        skip:
          !!!rawQueryParams?.store_name ||
          !!!rawQueryParams?.start_date ||
          !!!rawQueryParams?.end_date ||
          !!!rawQueryParams?.product_id,
      }
    );

  const summaryParams = querystring.stringify(
    omit({ ...rawQueryParams }, ['product_id'])
  );

  const { data: relationsSummary, isFetching } = useGetRelationsSummaryQuery(
    { serializedParams: summaryParams, body: filtersApiPayload },
    {
      skip:
        !!!rawQueryParams.store_name ||
        !!!rawQueryParams.start_date ||
        !!!rawQueryParams.end_date,
    }
  );

  return (
    <RelationsContext.Provider
      value={{
        selectedProduct,
        setSelectedProduct,
        productList,
        productListIsLoading: productListLoading,
        crossSell,
        crossSellIsLoading: crossSellLoading,
        replenishment,
        replenishmentIsLoading: replenishmentLoading,
        relationsSummary,
        relationsSummaryIsLoading: isFetching,
      }}
    >
      {children}
    </RelationsContext.Provider>
  );
};
